/**
 * Zoom Client View iframe host page.
 *
 * Rendered ONLY inside an `<iframe>` from MeetingRoom (parent: `/usabg-opportunity`).
 * The page is intentionally a bare-shell — no app chrome, no DashboardLayout,
 * no sidebar — because Client View mounts `#zmmtg-root` directly to <body>
 * with `position: fixed` and takes the full viewport. Hosting it in its own
 * route inside an iframe is the only way to preserve the parent's split-pane
 * layout (Zoom left, enrollment right).
 *
 * Parent ↔ child contract (same-origin postMessage):
 *
 *   child → parent
 *     { type: 'zoom-host:connection-change', state: 'connecting'|'connected'|'ended'|'failed', reason? }
 *
 *   parent → child
 *     { type: 'zoom-host:leave' }
 *
 * URL params (from parent's iframe src):
 *   sdkJwt  — SDK JWT (mn role iat exp tokenExp signed against sdkSecret)
 *   mn      — meeting number (string)
 *   pw      — meeting passcode (may be empty)
 *   un      — display name shown in the meeting
 *   ue      — user email
 *   role    — '0' attendee, '1' host
 *   leave   — leaveUrl: where Zoom navigates the iframe on Leave (we send
 *             this to a /meeting-ended stub so the parent gets a clean
 *             `state:'ended'` signal)
 */

import React from 'react'
import { loadClientViewSdk, ZOOM_VERSION } from './clientViewLoader'

import './ZoomHostPage.scss'

const REQUIRED_PARAMS = ['sdkJwt', 'mn', 'un']

const postToParent = payload => {
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(payload, window.location.origin)
    }
  } catch (e) {
    // Best-effort. If the parent navigated away or the origin changed, the
    // call throws — there's nothing meaningful to do.
  }
}

const readParams = () => {
  const p = new URLSearchParams(window.location.search)
  return {
    sdkJwt: p.get('sdkJwt'),
    mn: p.get('mn'),
    pw: p.get('pw') || '',
    un: p.get('un'),
    ue: p.get('ue') || '',
    role: parseInt(p.get('role') || '0', 10) || 0,
    leave: p.get('leave') || `${window.location.origin}/usabg-opportunity/meeting-ended`,
  }
}

class ZoomHostPage extends React.Component {
  state = { error: null, hideLoading: false }

  _params = null
  _onParentMessage = null

  componentDidMount() {
    this._mounted = true
    document.documentElement.classList.add('zoom-host-shell')
    document.body.classList.add('zoom-host-shell')
    this._params = readParams()

    const missing = REQUIRED_PARAMS.filter(k => !this._params[k])
    if (missing.length) {
      postToParent({
        type: 'zoom-host:connection-change',
        state: 'failed',
        reason: `missing-params:${missing.join(',')}`,
      })
      this.setState({ error: `Missing required params: ${missing.join(', ')}` })
      return
    }

    this._onParentMessage = event => {
      if (event.origin !== window.location.origin) return
      const data = event.data || {}
      if (data.type === 'zoom-host:leave') {
        try {
          if (window.ZoomMtg && typeof window.ZoomMtg.leaveMeeting === 'function') {
            window.ZoomMtg.leaveMeeting(false)
          }
        } catch (e) {}
      }
    }
    window.addEventListener('message', this._onParentMessage)

    this._boot().catch(ex => {
      if (!this._mounted) return
      this.setState({ error: ex && ex.message ? ex.message : 'Unknown error' })
      postToParent({
        type: 'zoom-host:connection-change',
        state: 'failed',
        reason: 'boot-error',
      })
    })
  }

  componentWillUnmount() {
    this._mounted = false
    document.documentElement.classList.remove('zoom-host-shell')
    document.body.classList.remove('zoom-host-shell')
    if (this._onParentMessage) {
      window.removeEventListener('message', this._onParentMessage)
      this._onParentMessage = null
    }
    try {
      if (window.ZoomMtg && typeof window.ZoomMtg.endMeeting === 'function') {
        window.ZoomMtg.endMeeting()
      }
    } catch (e) {}
  }

  _boot = async () => {
    const ZoomMtg = await loadClientViewSdk()

    ZoomMtg.setZoomJSLib(`https://source.zoom.us/${ZOOM_VERSION}/lib`, '/av')
    ZoomMtg.preLoadWasm()
    if (typeof ZoomMtg.prepareWebSDK === 'function') ZoomMtg.prepareWebSDK()
    else if (typeof ZoomMtg.prepareJssdk === 'function') ZoomMtg.prepareJssdk()

    // Subscribe to status updates BEFORE init so we don't miss the first
    // 'connecting' callback. inMeetingServiceListener is documented to be
    // safe to call pre-init.
    if (typeof ZoomMtg.inMeetingServiceListener === 'function') {
      ZoomMtg.inMeetingServiceListener('onMeetingStatus', payload => {
        // payload.meetingStatus: 1 connecting, 2 connected, 3 disconnected, 4 reconnecting
        const status = payload && (payload.meetingStatus || payload.status)
        const stateMap = {
          1: 'connecting',
          2: 'connected',
          3: 'ended',
          4: 'failed',
        }
        const state = stateMap[status]
        if (!state) return
        postToParent({
          type: 'zoom-host:connection-change',
          state,
          raw: payload,
        })
        if (this._mounted && (state === 'connected' || state === 'connecting')) {
          this.setState({ hideLoading: true })
        }
      })
    }

    await new Promise((resolve, reject) => {
      ZoomMtg.init({
        leaveUrl: this._params.leave,
        patchJsMedia: true,
        success: resolve,
        error: reject,
      })
    })

    await new Promise((resolve, reject) => {
      ZoomMtg.join({
        sdkKey: process.env.REACT_APP_ZOOM_MEETING_SDK_KEY,
        signature: this._params.sdkJwt,
        meetingNumber: this._params.mn,
        userName: this._params.un,
        userEmail: this._params.ue,
        passWord: this._params.pw,
        success: resolve,
        error: err => {
          postToParent({
            type: 'zoom-host:connection-change',
            state: 'failed',
            reason: 'join-error',
            raw: err,
          })
          reject(err)
        },
      })
    })
  }

  render() {
    const { error, hideLoading } = this.state
    return (
      <>
        <div id='zmmtg-root' />
        {!hideLoading && (
          <div className='zoom-host__loading' role='status' aria-live='polite'>
            {error ? (
              <div className='zoom-host__error'>
                <p>We couldn't start the meeting.</p>
                <small>{error}</small>
              </div>
            ) : (
              <>
                <div className='zoom-host__spinner' aria-hidden='true' />
                <p>Connecting…</p>
              </>
            )}
          </div>
        )}
      </>
    )
  }
}

export default ZoomHostPage
